// /app/frontend/src/pages/seo/BlackLoveDatingAppPage.jsx
// Focus: Cultural anchor — Black love as identity, intergenerational, family-building.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function BlackLoveDatingAppPage() {
  const path = '/black-love-dating-app';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Black Love Dating App', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Love Dating App | Meaningful Relationships & Community"
        description="UnityLove is a dating and community platform designed for Black singles, couples, and families seeking meaningful relationships, authentic connections, and shared values."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Community"
        headline="Black Love Starts With Intentional Connection"
        sub="A dating and community platform for Black singles, couples, and families building meaningful relationships rooted in culture, care, and shared vision."
        body="Bring your full self. Find people who see you. Build something that lasts."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse All Guides', to: '/guides' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why this exists */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Black Love Deserves Its Own Space
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Most dating apps were not designed with Black people in mind. The defaults — algorithms, photo filters, language patterns, harassment dynamics — were built around a different user, and the experience often reflects that. For Black singles and couples, this can mean spending years dating people who do not quite see you, or filtering through space that asks you to translate yourself constantly.
            </p>
            <p>
              UnityLove is built around the assumption that you are already whole — culturally, spiritually, romantically — and that the work of dating is finding people whose wholeness fits yours. Not explaining yourself. Not editing yourself. Showing up fully and finding the people who recognize you.
            </p>
          </div>
        </section>

        {/* What centering means */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What &ldquo;Centering Black Love&rdquo; Actually Means
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🌍"
              accent="amber"
              title="Shared Cultural Memory"
              body="Conversations do not start with explanation. Food, family, faith, music, the way home feels — all part of the conversation, not the back-story you have to provide."
            />
            <FeatureCard
              icon="💛"
              accent="rose"
              title="Intergenerational Love"
              body="Healthy Black love is rarely just two people — it is family, lineage, community. Our profile prompts and community spaces invite the whole picture, not just the dating profile snapshot."
            />
            <FeatureCard
              icon="🤝"
              accent="emerald"
              title="Community-Driven Connection"
              body="Lounges, themed conversation spaces, and verified members create a community around dating — not a marketplace. People who are part of something tend to treat each other better."
            />
            <FeatureCard
              icon="🏡"
              accent="sky"
              title="Singles, Couples, And Families"
              body="Not just singles looking to pair. Couples exploring together. Families building plural structures. Whatever shape your love takes, this is a space for it."
            />
            <FeatureCard
              icon="🛡️"
              accent="violet"
              title="Verified, Real People"
              body="Identity verification through Stripe Identity protects the community from catfishing, bots, and fetishization — common harms Black users have lived with on mainstream apps."
            />
            <FeatureCard
              icon="🎯"
              accent="amber"
              title="Shared Values, Not Just Vibes"
              body="Profile fields go beyond bio and photo — values, vision for family, relationship style, faith framing. The conversations that follow are immediately deeper because the foundation is honest."
            />
          </div>
        </section>

        {/* Realities */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            The Realities We Take Seriously
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Black dating online has a documented history of being harder — slower replies for Black women, fetishization of Black men, racial filters quietly built into ranking algorithms on some major apps. None of this is a secret to the people living it. The shift starts when a platform is built with these realities in mind from the beginning, instead of treating them as edge cases to patch later.
            </p>
            <p>
              UnityLove makes some specific choices: verification is encouraged early, moderation policies explicitly address racialized fetishization, and the community spaces are run with the kind of care most apps do not invest in. None of this is perfect. All of it is the difference between a space that was designed for Black users and one that was opened up to them.
            </p>
          </div>
        </section>

        {/* For everyone in Black love */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            For Everyone In Black Love
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Black singles seeking long-term partnership. Black couples exploring polyamory together. Black families building plural structures. Black LGBTQ+ members finding people who hold all of their identity at once. Black members of the diaspora finding people who share the cultural reference points — or do not, and connect across them anyway.
            </p>
            <p>
              All of it belongs here. None of it is the exception.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/african-diaspora-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">African Diaspora Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connecting across the global Black community.</p>
            </a>
            <a href="/black-sister-wife-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Sister Wife Dating</p>
              <p className="text-sm text-slate-600 mt-1">A community for Black plural family-building.</p>
            </a>
            <a href="/verified-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Verified Dating App</p>
              <p className="text-sm text-slate-600 mt-1">How verification protects real connections.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">For people looking for more than casual.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
